import React, { useState, useRef } from "react";
import { generateItinerary } from "./api/tripApi";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm"; // Enables GitHub-Flavored Markdown
import { FaMapMarkerAlt, FaPlaneDeparture, FaCalendarAlt, FaDollarSign, FaUtensils, FaUsers, FaClipboard } from 'react-icons/fa';
import { Helmet } from "react-helmet";

const getNextSaturday = (daysAhead) => {
  let date = new Date();
  date.setDate(date.getDate() + daysAhead); // Move forward 14 days

  while (date.getDay() !== 6) { // 6 means Saturday
    date.setDate(date.getDate() + 1);
  }

  return date.toISOString().split("T")[0]; // Format YYYY-MM-DD for input type="date"
};

const getDateTo = (dateFrom) => {
  let date = new Date(dateFrom);
  date.setDate(date.getDate() + 6); // Add 6 days
  return date.toISOString().split("T")[0];
};

const defaultDateFrom = getNextSaturday(14);
const defaultDateTo = getDateTo(defaultDateFrom);

// Helper to extract destinations from AI markdown table..
function extractDestinationsFromMarkdown(markdown) {
  // Find the first markdown table in the string
  const tableMatch = markdown.match(/\|([.|\n])*?\|/g);
  if (!tableMatch) return [];
  // Split into lines
  const lines = markdown.split('\n').filter(line => line.trim().startsWith('|'));
  if (lines.length < 3) return [];
  // Remove header and separator
  const dataLines = lines.slice(2);
  // Extract first column (destination), strip markdown
  return dataLines.map(line => {
    const cols = line.split('|').map(s => s.trim());
    let dest = cols[1] || null;
    if (dest) {
      // Remove markdown bold/italic
      dest = dest.replace(/\*\*|__|\*|_/g, '').trim();
    }
    return dest;
  }).filter(Boolean);
}

// Helper to extract the first markdown table from the AI output
function extractTableFromMarkdown(markdown) {
  // Match the first markdown table (lines starting and ending with |)
  const tableRegex = /(\|.+\n)+(\|.+\|)/;
  const match = markdown.match(tableRegex);
  return match ? match[0].trim() : '';
}

// Helper to bold the destination in the best fit text
function boldDestination(text) {
  // Try to bold the first phrase before a period, colon, or 'because'
  return text.replace(/^([^.\n:]+?)([.,:\n\s-]+)(because|as|since|for|$)/i, '**$1**$2$3');
}

// Helper to extract sections from AI output when destination is provided
function extractSectionsFromItinerary(markdown) {
  // Remove the quote section first to avoid it being included in other sections
  let quote = '';
  let main = markdown;
  const quoteRegex = /(?:motivational travel quote|motivational quote|quote)[^\n]*[: -]?\s*([\s\S]*?)(?=\n{2,}|$)/i;
  const quoteMatch = main.match(quoteRegex);
  if (quoteMatch) {
    quote = quoteMatch[1].trim();
    // Remove the quote section from the main text
    main = main.replace(quoteMatch[0], '');
  }
  // Extract summary (first paragraph)
  const summaryMatch = main.match(/^(.*?)(?:\n{2,}|\n- |\n\*\*|\n#|$)/s);
  const summary = summaryMatch ? summaryMatch[1].trim() : '';
  // Extract highlights (bulleted or numbered list after summary)
  const highlightsMatch = main.match(/(?:highlights|must-see|unique experiences)[^\n]*:?\s*([\s\S]*?)(?=\n{2,}|\n\*\*|\n#|packing|tips|itinerary|table|$)/i);
  const highlights = highlightsMatch ? highlightsMatch[1].trim() : '';
  // Extract packing/weather tips
  const tipsMatch = main.match(/(?:packing|weather)[^\n]*:?\s*([\s\S]*?)(?=\n{2,}|\n\*\*|\n#|itinerary|table|hidden gem|checklist|$)/i);
  const tips = tipsMatch ? tipsMatch[1].trim() : '';
  // Extract itinerary table
  const tableMatch = main.match(/((?:\|.+\n)+\|.+\|)/);
  const table = tableMatch ? tableMatch[0].trim() : '';
  // Extract hidden gems
  const gemsMatch = main.match(/(?:hidden gem|off the beaten path|local experiences)[^\n]*:?\s*([\s\S]*?)(?=\n{2,}|\n\*\*|\n#|checklist|$)/i);
  const gems = gemsMatch ? gemsMatch[1].trim() : '';
  // Extract packing checklist
  const checklistMatch = main.match(/(?:packing checklist)[^\n]*:?\s*([\s\S]*?)(?=\n{2,}|\n\*\*|\n#|$)/i);
  const checklist = checklistMatch ? checklistMatch[1].trim() : '';
  return { summary, highlights, tips, table, gems, checklist, quote };
}

// Helper to clean up the summary section (remove markdown heading if present)
function cleanSummary(summary) {
  // Remove markdown headings like '### Trip Summary' or similar
  return summary.replace(/^#+\s*Trip Summary\s*/i, '').trim();
}

// Helper to convert <br> and <br/> to markdown line breaks
function convertBrToMarkdown(text) {
  return text.replace(/<br\s*\/?>/gi, '  \n');
}

// Helper to clean up markdown code block wrappers
function stripMarkdownCodeBlock(text) {
  return text.replace(/^```markdown\n?|```$/gim, '').trim();
}

// Helper to extract motivational quote from blockquote or quote pattern
function extractMotivationalQuote(markdown) {
  // Try to find a blockquote (starts with >)
  const blockquoteMatch = markdown.match(/^>\s*"([^"]+)"\s*[–-]?\s*([^\n]*)/m);
  if (blockquoteMatch) {
    return blockquoteMatch[1] + (blockquoteMatch[2] ? ' – ' + blockquoteMatch[2] : '');
  }
  // Try to find a line with "quote" or "motivational" in it
  const quoteMatch = markdown.match(/(?:motivational travel quote|motivational quote|quote)[^\n]*[: -]?\s*([\s\S]*?)(?=\n{2,}|$)/i);
  if (quoteMatch) {
    return quoteMatch[1].replace(/^>\s*/, '').trim();
  }
  return '';
}

// Helper to remove markdown bold/italic asterisks/underscores from a string
function stripMarkdownAsterisks(text) {
  return text.replace(/\*\*|__|\*|_/g, '').trim();
}

function TripPlanner() {
  const [formData, setFormData] = useState({
    origin: "Tampa, FL, USA",
    destination: "",
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
    budget: "$3000",
    foodPreferences: "Vegetarian",
    numPeople: "2",
    destinationType: "anywhere",
  });

  const [itinerary, setItinerary] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const itineraryRef = useRef(null);
  const [pendingDestination, setPendingDestination] = useState(null);

  // PayPal donation URL - same as MatchScheduler
  const paypalDonateUrl = "https://www.paypal.com/donate/?hosted_button_id=FGPNV7YTRPPVE";

  // Helper to check if all fields are empty
  const isFormEmpty = Object.values(formData).every(val => !val);

  const handleDonate = () => {
    if (window.gtag) {
      window.gtag('event', 'donate_click', {
        event_category: 'engagement',
        event_label: 'Donate Button'
      });
    }
    window.open(paypalDonateUrl, '_blank');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (window.gtag) {
      window.gtag('event', 'generate_itinerary_click', {
        event_category: 'engagement',
        event_label: formData.destination ? 'With Destination' : 'With Suggestions'
      });
    }
    setLoading(true);
    setError("");
    setItinerary("");

    // Basic validation for date range
    if (formData.dateFrom && formData.dateTo && formData.dateFrom > formData.dateTo) {
      setError("Start date cannot be after end date.");
      setLoading(false);
      return;
    }

    try {
      // Combine the dates into a single string to send to backend
      const dates = `${formData.dateFrom} to ${formData.dateTo}`;
      const result = await generateItinerary({ ...formData, dates, destinationType: formData.destinationType });
      setItinerary(result);
      setTimeout(() => {
        itineraryRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100); // Give a brief delay to allow render
      if (window.gtag) {
        window.gtag('event', 'form_submission', {
          event_category: 'form',
          event_label: formData.destination ? 'With Destination' : 'With Suggestions'
        });
      }
    } catch (err) {
      setError("Failed to generate itinerary. Please try again.");
    }

    setLoading(false);
  };

  const handleReset = () => {
    setFormData({
      origin: "",
      destination: "",
      dateFrom: "",
      dateTo: "",
      budget: "",
      foodPreferences: "",
      numPeople: "",
      destinationType: "anywhere",
    });
    setItinerary("");
    setError("");
  };

  const handleCopy = () => {
    if (window.gtag) {
      window.gtag('event', 'copy_itinerary', {
        event_category: 'engagement',
        event_label: 'Copy All Button'
      });
    }
    navigator.clipboard.writeText(itinerary).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // When a destination is clicked from suggestions
  const handleDestinationClick = async (dest) => {
    if (window.gtag) {
      window.gtag('event', 'destination_suggestion_click', {
        event_category: 'engagement',
        event_label: dest
      });
    }
    setFormData(prev => ({ ...prev, destination: dest }));
    setPendingDestination(dest);
    setLoading(true);
    setError("");
    setItinerary("");
    try {
      const dates = `${formData.dateFrom} to ${formData.dateTo}`;
      const result = await generateItinerary({ ...formData, destination: dest, dates, destinationType: formData.destinationType });
      setItinerary(result);
      setTimeout(() => {
        itineraryRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      if (window.gtag) {
        window.gtag('event', 'form_submission', {
          event_category: 'form',
          event_label: 'Suggestion Clicked: ' + dest
        });
      }
    } catch (err) {
      setError("Failed to generate itinerary. Please try again.");
    }
    setLoading(false);
    setPendingDestination(null);
  };

  return (
    <>
      <Helmet>
        <title>AI Trip Planner | Free Custom Itineraries & Packing Lists</title>
        <meta name="description" content="Plan your next adventure with our free AI-powered trip planner. Get custom itineraries, packing lists, and travel tips for families, friends, and solo travelers. Fast, easy, and mobile-friendly." />
        <link rel="canonical" href="https://truth-ai-tech.com/trip-planner" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 font-sans">
        <div className="max-w-3xl mx-auto py-10 px-4">
          <h1 className="text-3xl md:text-4xl font-bold text-blue-900 mb-2 text-center">AI Trip Planner</h1>
          <p className="text-lg text-blue-800 mb-6 text-center">Get instant, personalized travel itineraries, packing checklists, and local tips for any destination. Powered by AI, free for all users!</p>
        {/* Donation Section ..*/}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl shadow-lg p-6 mb-0">
            <div className="flex flex-col md:flex-row items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <img 
                  src="/logo.png" 
                  alt="Truth Logo" 
                  className="h-12 w-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <span 
                  className="text-2xl font-bold text-white hidden"
                  style={{ display: 'none' }}
                >
                  Truth
                </span>
              </div>
              <div className="flex flex-col items-center text-center">
                <h3 className="text-xl font-bold text-white mb-2">☕ Support Truth</h3>
                <p className="text-yellow-100 text-sm">If this tool helps you, consider buying me a coffee!</p>
              </div>
              <button
                onClick={handleDonate}
                className="bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition shadow-md flex items-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.067 8.478c.492.315.844.825.844 1.522 0 .697-.352 1.207-.844 1.522-.492.315-1.156.478-1.844.478H5.777c-.688 0-1.352-.163-1.844-.478C3.441 11.207 3.089 10.697 3.089 10c0-.697.352-1.207.844-1.522.492-.315 1.156-.478 1.844-.478h12.446c.688 0 1.352.163 1.844.478z"/>
                </svg>
                Donate with PayPal
              </button>
            </div>
          </div>
          </div>

        {/* Trip Planner Section */}
        <div style={styles.card}>
          <div style={styles.headerBar}>
            <h1 style={styles.title}>Trip Planner <span style={styles.betaTag}>BETA</span></h1>
          </div>
          <form onSubmit={handleSubmit} style={styles.form}>
            <label style={styles.label}>
              <span style={styles.labelRow}>
                <FaMapMarkerAlt style={styles.icon} />
                <span>Origin</span>
              </span>
              <input
                name="origin"
                placeholder="City or Airport"
                value={formData.origin}
                onChange={handleChange}
                required
                style={styles.input}
              />
              <span style={styles.helper}>Where are you starting your journey?</span>
            </label>

            <label style={styles.label}>
              <span style={styles.labelRow}>
                <FaPlaneDeparture style={styles.icon} />
                <span>Destination <span style={{ color: '#888', fontWeight: 400 }}>(optional)</span></span>
              </span>
              <input
                name="destination"
                placeholder="City or Airport (leave blank for suggestions)"
                value={formData.destination}
                onChange={handleChange}
                style={styles.input}
              />
              <span style={styles.helper}>Leave blank to get top destination ideas for your dates and budget!</span>
            </label>

            {(!formData.destination || formData.destination.trim() === "") && (
              <label style={styles.label}>
                <span style={styles.labelRow}>
                  <span>Destination Type</span>
                </span>
                <select
                  name="destinationType"
                  value={formData.destinationType}
                  onChange={handleChange}
                  style={{ ...styles.input, width: '100%', maxWidth: 320 }}
                >
                  <option value="anywhere">Anywhere (International & Domestic)</option>
                  <option value="domestic">Only within my country</option>
                </select>
                <span style={styles.helper}>Choose if you want suggestions from anywhere or only within your country.</span>
              </label>
            )}

            <label style={styles.label}>
              <span style={styles.labelRow}>
                <FaCalendarAlt style={styles.icon} />
                <span>Date From</span>
              </span>
              <input
                type="date"
                name="dateFrom"
                value={formData.dateFrom}
                onChange={handleChange}
                required
                style={styles.input}
              />
            </label>

            <label style={styles.label}>
              <span style={styles.labelRow}>
                <FaCalendarAlt style={styles.icon} />
                <span>Date To</span>
              </span>
              <input
                type="date"
                name="dateTo"
                value={formData.dateTo}
                onChange={handleChange}
                required
                style={styles.input}
              />
            </label>

            <label style={styles.label}>
              <span style={styles.labelRow}>
                <FaDollarSign style={styles.icon} />
                <span>Budget</span>
              </span>
              <input
                name="budget"
                placeholder="Your budget (e.g. $1500)"
                value={formData.budget}
                onChange={handleChange}
                required
                style={styles.input}
              />
              <span style={styles.helper}>Total budget for the group</span>
            </label>

            <label style={styles.label}>
              <span style={styles.labelRow}>
                <FaUtensils style={styles.icon} />
                <span>Food Preferences</span>
              </span>
              <input
                name="foodPreferences"
                placeholder="e.g. vegetarian, vegan, no pork"
                value={formData.foodPreferences}
                onChange={handleChange}
                style={styles.input}
              />
              <span style={styles.helper}>Optional: dietary needs or restrictions</span>
            </label>

            <label style={styles.label}>
              <span style={styles.labelRow}>
                <FaUsers style={styles.icon} />
                <span>Number of People</span>
              </span>
              <input
                type="number"
                name="numPeople"
                placeholder="e.g. 2"
                value={formData.numPeople}
                onChange={handleChange}
                required
                min="1"
                style={styles.input}
              />
            </label>

            <div style={styles.buttons}>
              <button type="submit" disabled={loading} style={styles.button}>
                {loading ? "Generating..." : "Generate Itinerary"}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={loading || isFormEmpty}
                style={{
                  ...styles.button,
                  backgroundColor: isFormEmpty ? "#eee" : "#f3f4f6",
                  color: isFormEmpty ? "#aaa" : "#333",
                  marginLeft: 10,
                  cursor: isFormEmpty ? "not-allowed" : "pointer",
                  border: '1px solid #ccc',
                  fontWeight: 500,
                }}
              >
                Reset
              </button>
            </div>
            {loading && (
              <div style={styles.modalOverlay} aria-modal="true" role="dialog" aria-busy="true">
                <div style={styles.modalContent}>
                  <img src="/A.I-2.gif" alt="Loading..." style={styles.loader} draggable="false" />
                  <p style={{ color: "#fff", marginTop: 10 }}>Planning your trip...</p>
                </div>
              </div>
            )}
          </form>
          {error && <p style={styles.error}>{error}</p>}
          {itinerary && (
            <div ref={itineraryRef} style={styles.resultContainer}>
              <div style={styles.resultHeader}>
                <h2 style={styles.resultTitle}>Your AI-Powered Itinerary</h2>
                <button onClick={handleCopy} style={styles.copyButton} title="Copy All">
                  <FaClipboard style={{ marginRight: 6 }} />
                  {copied ? "Copied!" : "Copy All"}
                </button>
              </div>
              <div style={styles.markdownContainer}>
                {/* Only show the main itinerary if a destination is selected or user has clicked a suggestion */}
                {formData.destination ? (
                  <div ref={itineraryRef} style={styles.resultContainer}>
                    {/* Summary/Intro */}
                    {extractSectionsFromItinerary(itinerary).summary && (
                      <div style={{ background: '#e6f7ff', borderRadius: 10, padding: 24, marginBottom: 24, border: '1px solid #91d5ff', boxShadow: '0 2px 8px rgba(0,123,255,0.06)' }}>
                        <div style={{ fontWeight: 800, fontSize: 22, color: '#007bff', marginBottom: 10, letterSpacing: 0.5 }}>Trip Summary</div>
                        <div style={{ fontSize: 19, color: '#222', lineHeight: 1.7, fontWeight: 500 }}>{cleanSummary(extractSectionsFromItinerary(itinerary).summary)}</div>
                      </div>
                    )}
                    {/* Highlights */}
                    {extractSectionsFromItinerary(itinerary).highlights && (
                      <div style={{ marginBottom: 18 }}>
                        <div style={{ fontWeight: 800, fontSize: 18, color: '#007bff', marginBottom: 6 }}>Must-See Highlights</div>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{extractSectionsFromItinerary(itinerary).highlights}</ReactMarkdown>
                      </div>
                    )}
                    {/* Packing/Weather Tips */}
                    {extractSectionsFromItinerary(itinerary).tips && (
                      <div style={{ background: '#fffbe6', borderRadius: 8, padding: 16, marginBottom: 18, border: '1px solid #ffe58f' }}>
                        <div style={{ fontWeight: 800, fontSize: 18, color: '#b8860b', marginBottom: 6 }}>Packing & Weather Tips</div>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{extractSectionsFromItinerary(itinerary).tips}</ReactMarkdown>
                      </div>
                    )}
                    {/* Itinerary Table */}
                    {extractSectionsFromItinerary(itinerary).table && (
                      <div style={{ marginBottom: 18 }}>
                        <div style={{ fontWeight: 800, fontSize: 18, color: '#007bff', marginBottom: 6 }}>Day-by-Day Itinerary</div>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            table: ({node, ...props}) => <table style={styles.table} {...props} />, 
                            th: ({node, ...props}) => <th style={styles.th} {...props} />, 
                            td: ({node, ...props}) => <td style={styles.td} {...props} />
                          }}
                        >
                          {extractSectionsFromItinerary(itinerary).table}
                        </ReactMarkdown>
                      </div>
                    )}
                    {/* Hidden Gems */}
                    {extractSectionsFromItinerary(itinerary).gems && (
                      <div style={{ background: '#f6ffed', borderRadius: 8, padding: 16, marginBottom: 18, border: '1px solid #b7eb8f' }}>
                        <div style={{ fontWeight: 800, fontSize: 18, color: '#389e0d', marginBottom: 6 }}>Hidden Gems & Local Experiences</div>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{extractSectionsFromItinerary(itinerary).gems}</ReactMarkdown>
                      </div>
                    )}
                    {/* Packing Checklist */}
                    {extractSectionsFromItinerary(itinerary).checklist && (
                      <div style={{ background: '#f0f5ff', borderRadius: 8, padding: 16, marginBottom: 18, border: '1px solid #adc6ff' }}>
                        <div style={{ fontWeight: 800, fontSize: 18, color: '#2f54eb', marginBottom: 6 }}>Packing Checklist</div>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{extractSectionsFromItinerary(itinerary).checklist}</ReactMarkdown>
                      </div>
                    )}
                    {/* Motivational Quote */}
                    {extractSectionsFromItinerary(itinerary).quote && (
                      <div style={{ background: '#e6f7ff', borderRadius: 8, padding: 16, marginBottom: 0, border: '1px solid #91d5ff', fontStyle: 'italic', color: '#007bff', fontWeight: 600, fontSize: 17 }}>
                        <span style={{ fontWeight: 800, color: '#007bff', fontSize: 18, marginRight: 8 }}>Motivational Quote:</span>
                        {stripMarkdownAsterisks(extractSectionsFromItinerary(itinerary).quote)}
                      </div>
                    )}
                    {/* Copy Button */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
                      <button onClick={handleCopy} style={styles.copyButton} title="Copy All">
                        <FaClipboard style={{ marginRight: 6 }} />
                        {copied ? "Copied!" : "Copy All"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
          {/* Show destination suggestions only if no destination is selected */}
          {itinerary && !formData.destination && (() => {
            // Clean up markdown code block wrappers
            const cleanItinerary = stripMarkdownCodeBlock(itinerary);
            const destinations = extractDestinationsFromMarkdown(cleanItinerary);
            if (destinations.length === 0) return null;

            // Extract just the table
            const tableMarkdown = extractTableFromMarkdown(cleanItinerary);
            // Extract 'Best Fit Recommendation' and 'Motivational Quote' from the AI output
            const bestFitMatch = cleanItinerary.match(/(?:best fit|recommended destination)[^\n]*[: -]?\s*([\s\S]*?)(?=\n{2,}|\n\*\*|\n#|Motivational Travel Quote|Motivational Quote|quote|$)/i);
            const bestFit = bestFitMatch ? bestFitMatch[1].trim() : null;
            // Use improved quote extraction
            const quote = extractMotivationalQuote(cleanItinerary);
            const bestFitText = bestFit ? boldDestination(bestFit) : null;

            return (
              <div style={{ marginTop: 24, background: '#e6f7ff', borderRadius: 8, padding: 16, border: '1px solid #b3e0ff' }}>
                <h3 style={{ color: '#007bff', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Not sure where to go?</h3>
                <p style={{ color: '#333', marginBottom: 8 }}>Here are some top destinations for your trip! Click a destination to plan a detailed itinerary for it.</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {destinations.map((dest, idx) => (
                    <li key={dest}>
                      <button
                        onClick={() => handleDestinationClick(dest)}
                        style={{
                          background: '#fff',
                          color: '#007bff',
                          border: '1px solid #b3e0ff',
                          borderRadius: 6,
                          padding: '8px 18px',
                          fontWeight: 600,
                          fontSize: 16,
                          cursor: 'pointer',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                          opacity: pendingDestination === dest ? 0.6 : 1,
                        }}
                        disabled={pendingDestination === dest || loading}
                      >
                        {pendingDestination === dest ? 'Loading...' : dest}
                      </button>
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: 18 }}>
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      table: ({node, ...props}) => <table style={styles.table} {...props} />, 
                      th: ({node, ...props}) => <th style={styles.th} {...props} />, 
                      td: ({node, ...props}) => <td style={styles.td} {...props} />
                    }}
                  >
                    {convertBrToMarkdown(tableMarkdown)}
                  </ReactMarkdown>
                </div>
                {/* Best Fit Recommendation Section */}
                {bestFitText && (
                  <div style={{ margin: '32px 0 0 0', padding: '18px 20px', background: '#fffbe6', borderRadius: 8, border: '1px solid #ffe58f' }}>
                    <div style={{ fontWeight: 800, fontSize: 20, color: '#b8860b', marginBottom: 8, fontFamily: 'inherit' }}>Best Fit Recommendation</div>
                    <div style={{ fontSize: 16, color: '#333', fontFamily: 'inherit' }}>
                      <ReactMarkdown>{bestFitText}</ReactMarkdown>
                    </div>
                  </div>
                )}
                {/* Motivational Quote Section */}
                {quote && (
                  <div style={{ margin: '32px 0 0 0', padding: '18px 20px', background: '#e6f7ff', borderRadius: 8, border: '1px solid #91d5ff' }}>
                    <div style={{ fontWeight: 800, fontSize: 20, color: '#007bff', marginBottom: 8, fontFamily: 'inherit' }}>Motivational Quote</div>
                    <div style={{ fontSize: 16, color: '#333', fontFamily: 'cursive, inherit' }}>{stripMarkdownAsterisks(quote)}</div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </>
  );
}

const styles = {
  donateSection: {
    background: 'linear-gradient(90deg, #FFD600 0%, #FF9800 100%)',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    padding: 24,
    margin: '0 auto 40px auto',
    maxWidth: 900,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
  },
  donateButton: {
    background: '#fff',
    color: '#FF9800',
    padding: '12px 28px',
    borderRadius: 10,
    fontWeight: 600,
    fontSize: 16,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    maxWidth: 900,
    margin: '0 auto',
    padding: 32,
    marginBottom: 40,
  },
  headerBar: {
    background: '#007bff',
    borderRadius: 12,
    margin: '-32px -32px 32px -32px',
    padding: '18px 0',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  title: {
    color: '#fff',
    fontWeight: 700,
    fontSize: 28,
    letterSpacing: 1,
    margin: 0,
    display: 'inline-block',
  },
  betaTag: {
    background: '#ff9800',
    color: 'white',
    fontSize: '12px',
    fontWeight: 'bold',
    borderRadius: '4px',
    padding: '2px 8px',
    marginLeft: '10px',
    letterSpacing: '1px',
    verticalAlign: 'middle',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    marginBottom: 0,
  },
  label: {
    marginBottom: 15,
    fontWeight: 600,
    color: '#555',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    fontSize: 16,
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 600,
    fontSize: 16,
    color: '#555',
    marginBottom: 2,
  },
  icon: {
    marginRight: 8,
    color: '#007bff',
    fontSize: 18,
    verticalAlign: 'middle',
  },
  helper: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
    marginLeft: 2,
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    fontSize: 16,
    borderRadius: 4,
    border: '1px solid #ccc',
    marginTop: 6,
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: 10,
    marginBottom: 0,
  },
  button: {
    padding: '10px 20px',
    fontSize: 16,
    borderRadius: 4,
    border: 'none',
    backgroundColor: '#007bff',
    color: 'white',
    cursor: 'pointer',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
    transition: 'background 0.2s',
  },
  error: {
    color: 'red',
    marginTop: 15,
    fontWeight: 600,
  },
  resultContainer: {
    marginTop: 30,
    padding: 24,
    backgroundColor: '#f8fafc',
    borderRadius: 10,
    boxShadow: '0 0 16px rgba(0,0,0,0.10)',
    border: '1px solid #e0e0e0',
  },
  resultHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  resultTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: '#007bff',
    margin: 0,
  },
  itinerary: {
    whiteSpace: 'pre-wrap',
    fontFamily: "'Courier New', Courier, monospace",
    color: '#222',
  },
  copyButton: {
    marginTop: 0,
    padding: '8px 16px',
    fontSize: 14,
    borderRadius: 4,
    border: 'none',
    backgroundColor: '#28a745',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modalContent: {
    backgroundColor: 'rgba(20, 20, 60, 0.85)',
    padding: 40,
    borderRadius: 10,
    textAlign: 'center',
    boxShadow: '0 0 20px rgba(0,0,0,0.2)',
  },
  loader: {
    width: 320,
    height: 180,
    maxWidth: '90vw',
    maxHeight: '40vh',
  },
  markdownContainer: {
    fontSize: 18,
    lineHeight: '1.8',
    width: '100%',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 20,
  },
  th: {
    padding: 8,
    textAlign: 'left',
    backgroundColor: '#007bff',
    color: '#fff',
    border: '1px solid #ccc',
  },
  td: {
    padding: 8,
    border: '1px solid #ccc',
    backgroundColor: '#f9f9f9',
  },
};

export default TripPlanner;